import { uniq } from 'lodash-es';
import { createSelector } from '@reduxjs/toolkit';
import { colors } from '@karnott/colors';
import { I18n } from '../i18n';
import { ColorItem } from '../slices/colors';
import { CustomCrop, selectCropIds, selectCropsById } from '../slices/crops';
import { getColorWithHash } from '../utils/colors';
import { buildCropLabel } from '../utils/crops';
import { selectParcelsAsArray } from './parcels';
import { selectUserUnits } from './users';

const selectCropsAsArray = createSelector([selectCropsById, selectCropIds], (cropByID, cropIDs) => {
  return (cropIDs || []).map((id) => cropByID[id]).filter((crop) => !!crop);
});

export const selectBasicCrops = createSelector([selectCropsAsArray], (crops) =>
  crops.filter((crop) => crop.cluster_id == undefined),
);

export const selectCustomCrops = createSelector([selectCropsAsArray, selectBasicCrops], (crops, basicCrops) => {
  const customCrops = crops.filter((crop) => crop.cluster_id != undefined);
  return customCrops.map((c): CustomCrop => {
    const baseCrop = basicCrops.find((bc) => c.crop_id === bc.crop_id);
    return { ...c, base_crop: baseCrop?.name };
  });
});

export const selectCropColorItems = createSelector([selectCropsAsArray, selectUserUnits], (crops, units) => {
  return crops
    .filter((crop) => !!crop.color && crop.parcel_count)
    .map((crop): ColorItem => ({
      id: String(crop.id),
      name: buildCropLabel(crop, units.area),
      color: getColorWithHash(crop.color),
    }))
    .concat([
      {
        id: '0',
        name: I18n.t('Crop.undefined'),
        color: getColorWithHash(colors('black')),
      },
    ]);
});

export const selectCropsOnFieldsAsArray = createSelector(
  [selectCropsById, selectParcelsAsArray, selectUserUnits],
  (cropsById, parcels, units) => {
    const cropOnParcelIds = uniq(parcels.filter((p) => p.current_crop_id).map((p) => p.current_crop_id!));
    return cropOnParcelIds
      .filter((id) => cropsById[id])
      .map((id) => ({ ...cropsById[id]!, label: buildCropLabel(cropsById[id]!, units.area) }));
  },
);
